import styled from 'styled-components';

import { ANIM, COLORS, GRADIENTS, MOBILE, PRODUCT } from '../../theme/tokens';
import { hoverWash } from '../../theme/mixins';
import { Text } from '../../components';

// --- Feature header -----------------------------------------------------------
export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const HeaderId = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const IconTile = styled.div`
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 17px;
  background: ${GRADIENTS.featureCard};
`;

export const IconArt = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

/* Same ink (and typography) as the feature name on the product root's cards. It used to take
 * `onAccent`, which is the ink for text sitting ON an accent fill and therefore stays white in BOTH
 * themes — as page-level text on the light theme's pale page it rendered white on white. */
export const HeaderName = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  color: ${COLORS.featureName};
`;

export const HeaderDesc = styled(Text)`
  font-size: 12px;
  line-height: 1.1;
  color: ${COLORS.textSubdued};
`;

export const LumioPill = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 48px;
  padding: 8px 20px;
  border: 1px solid ${COLORS.lumioBorder};
  border-radius: 9999px;
  background: transparent;
  cursor: pointer;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}

  ${MOBILE} {
    width: 100%;
  }
`;

export const LumioSpark = styled.img`
  width: 24px;
  height: 24px;
`;

export const LumioLabel = styled(Text)`
  font-size: 14px;
  background: ${GRADIENTS.lumioText};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

// --- Benchmark panel -------------------------------------------------------------
export const BenchmarkPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 16px;
  background: ${GRADIENTS.featureCard};
`;

export const PanelEyebrow = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  line-height: 1.1;
  text-transform: uppercase;
  color: ${COLORS.textSubtle};
`;

export const BenchmarkTitle = styled(Text)`
  font-size: 24px;
  font-weight: 500;
  line-height: 1;
  margin-bottom: 8px;
  color: ${COLORS.benchmarkAccent};
`;

// --- Configurations --------------------------------------------------------------
export const ConfigEyebrow = styled(PanelEyebrow)`
  display: block;
  margin-bottom: 10px;
`;

/** Darker well that holds the configurations + showcase, matching the product root's card
 *  container (PRODUCT.containerBg) so the page reads as the same "titled section over a card
 *  container". */
export const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px;
  border-radius: 18px;
  background: ${PRODUCT.containerBg};
`;

export const PlanRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;

  ${MOBILE} {
    flex-direction: column;
  }
`;

/** Selectable tier card. Reuses the standard clickable-card pattern (same as the
 *  Website-currency cards in casino settings): a theme-aware grey `surfaceFaint` fill
 *  + the shared `hoverWash` highlight, with the WHOLE card acting as the control. Only
 *  the selected card carries the orange ring + glow; unselected keeps a 1px transparent
 *  border so selecting never shifts the layout. Rendered as a <button> while the tier is
 *  selectable (`$interactive`) and as a plain <div> once the feature is enabled
 *  (read-only: no up/downgrade) — the consumer swaps the element via the `as` prop. */
export const PlanCard = styled.button<{ $selected: boolean; $dim?: boolean; $interactive?: boolean }>`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  /* Head (name + price) pinned to the top; the read-only "Current configuration" pill,
     when present, drops to the bottom. */
  justify-content: space-between;
  gap: 24px;
  margin: 0;
  padding: 24px;
  text-align: left;
  font: inherit;
  color: inherit;
  appearance: none;
  border-radius: 16px;
  border: 1px solid ${(p) => (p.$selected ? COLORS.planSelectedBorder : 'transparent')};
  background: ${(p) => (p.$selected ? `${GRADIENTS.planSelectedGlow}, ${COLORS.surfaceFaint}` : COLORS.surfaceFaint)};
  transition: border-color ${ANIM.INTERACT_MS}ms, background ${ANIM.INTERACT_MS}ms;
  cursor: ${(p) => (p.$interactive ? 'pointer' : 'default')};
  ${(p) => (p.$interactive ? hoverWash : '')}
  /* Read-only (enabled feature, non-owned tier): greyed + inert, no up/downgrade. */
  ${(p) => (p.$dim ? 'opacity: 0.45; pointer-events: none;' : '')}

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

// Top group: tier name with the price stacked directly beneath it.
export const PlanTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const PlanName = styled(Text)<{ $selected: boolean }>`
  font-size: 20px;
  line-height: 1.1;
  color: ${(p) => (p.$selected ? COLORS.planSelectedBorder : COLORS.ctaText)};
`;

export const PlanPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

export const PrevRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 14px;
`;

export const PrevSpacer = styled.div`
  height: 14px;
`;

export const PrevPrice = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  text-decoration: line-through;
  color: ${COLORS.textGhost};
`;

export const Savings = styled(Text)`
  font-size: 10px;
  color: ${COLORS.benefitPositive};
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
`;

export const Currency = styled.div`
  font-size: 12px;
  align-self: flex-start;
  padding-top: 2px;
  color: ${COLORS.textPale};
`;

export const Amount = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: ${COLORS.textPale};
`;

export const Term = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  padding-bottom: 2px;
  color: ${COLORS.textGhost};
`;

/** Drift note in the price slot of a tier the catalog offers but the brand's campaign does not
 *  sell ("Not priced yet"): amber like {@link PendingLabel}, sized like the {@link Term}. */
export const DriftNote = styled(Text)`
  font-size: 12px;
  font-weight: 500;
  padding-bottom: 2px;
  color: ${COLORS.warning};
`;

/** Orange check badge in the top-right corner of the selected tier card — the same
 *  selection marker the Website-currency cards use. Absolutely positioned so it never
 *  affects the card's flex layout. */
export const Check = styled.span`
  position: absolute;
  top: 16px;
  right: 16px;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 99px;
  background: ${COLORS.planSelectedBorder};
`;

export const SelectedPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  padding: 0 16px;
  border-radius: 9999px;
  background: ${COLORS.planCtaSelectedBg};
`;

export const SelectedLabel = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.benefitPositive};
`;

/** "Payment pending" pill label — the amber counterpart of {@link SelectedLabel}, marking the
 *  tier a still-unpaid invoice will activate once the payment is finished. */
export const PendingLabel = styled(SelectedLabel)`
  color: ${COLORS.warning};
`;

// --- Status bar -------------------------------------------------------------------
// The feature status bar: status + level on the left, the activate/deactivate CTA on
// the right. Sits in the configurations panel directly beneath the tier cards (it flows
// in the column, no longer pinned).
export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-radius: 16px;
  background: ${COLORS.footerBg};

  ${MOBILE} {
    flex-wrap: wrap;
  }
`;

export const FooterStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** Right-hand action group in the status bar. Holds the single Activate/Deactivate CTA, or,
 *  while a payment is pending, the "Cancel upgrade" + "Finish payment" pair. On mobile it wraps
 *  below the status and stacks full-width: the primary CTA on top, "Cancel upgrade" beneath
 *  (column-reverse — the pair renders Cancel first in the DOM, primary second). */
export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  ${MOBILE} {
    width: 100%;
    flex-direction: column-reverse;
    align-items: stretch;

    & > * {
      width: 100%;
    }
  }
`;

export const StatusChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 0;
`;

export const StatusDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
`;

export const StatusLabel = styled(Text)<{ $color: string }>`
  font-size: 12px;
  font-weight: 500;
  color: ${(p) => p.$color};
`;